import { useEffect, useRef } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import {
  applyAdjustmentPrefill,
  applyParentInheritance,
  buildAdjustmentNoteDefaults,
  type AdjustmentPrefill,
} from './adjustment-note-defaults';
import {
  ADJUSTMENT_NOTE_SUBMIT_FIELDS,
  type AdjustmentNoteSubmitValues,
} from './adjustment-note-schema';
import type { InvoiceRow } from '../types';

export interface UseAdjustmentFormArgs {
  readonly open: boolean;
  readonly noteType: 'Credit' | 'Debit';
  readonly side: 'buy' | 'sell';
  readonly parents: InvoiceRow[];
  readonly existingInvoice: InvoiceRow | null;
  readonly blOrderName?: string | null;
  readonly prefillParentId?: string;
  readonly prefill?: AdjustmentPrefill;
  // Seed form values from outside (e.g. the Final-settlement overpayment bridge
  // prefilling the credit amount). Applied last, over the computed defaults.
  readonly prefillValues?: Partial<AdjustmentNoteSubmitValues>;
}

export function findParent(parents: InvoiceRow[], id: string): InvoiceRow | null {
  if (id === '') return null;
  return parents.find((row) => row.id === id) ?? null;
}

function buildDefaultValues(args: UseAdjustmentFormArgs): AdjustmentNoteSubmitValues {
  const parent = findParent(
    args.parents,
    args.existingInvoice?.adjusts_invoice_id ?? args.prefillParentId ?? '',
  );
  const defaults = applyAdjustmentPrefill(
    buildAdjustmentNoteDefaults({
      noteType: args.noteType,
      side: args.side,
      parent,
      existing: args.existingInvoice,
      ...(args.blOrderName !== undefined ? { blOrderName: args.blOrderName } : {}),
    }),
    args.prefill,
  );
  return args.prefillValues !== undefined ? { ...defaults, ...args.prefillValues } : defaults;
}

export function useAdjustmentForm(args: UseAdjustmentFormArgs) {
  const form = useForm<AdjustmentNoteSubmitValues>({
    resolver: zodResolver(ADJUSTMENT_NOTE_SUBMIT_FIELDS),
    defaultValues: buildDefaultValues(args),
  });
  const lastOpen = useRef(false);
  useEffect(() => {
    if (!args.open) {
      lastOpen.current = false;
      return;
    }
    if (lastOpen.current) return;
    lastOpen.current = true;
    form.reset(buildDefaultValues(args));
  }, [
    args.open,
    args.noteType,
    args.side,
    args.parents,
    args.existingInvoice,
    args.blOrderName,
    args.prefillParentId,
    args.prefill,
    args.prefillValues,
    form,
  ]);
  const { getValues, setValue } = form;
  // The follow-on path can mount against a stale adjustable-parents cache —
  // e.g. the predecessor was issued moments ago, so the cached list predates
  // it and the row only arrives with the background refetch, after the
  // defaults were built. The parent field is disabled on that path, so the
  // operator can't recover by hand: seed it (and what it inherits) as soon as
  // the requested parent shows up.
  useEffect(() => {
    if (args.prefillParentId === undefined || args.existingInvoice !== null) return;
    if (getValues('adjusts_invoice_id') !== '') return;
    const parent = findParent(args.parents, args.prefillParentId);
    if (parent === null) return;
    setValue('adjusts_invoice_id', parent.id, { shouldValidate: true });
    applyParentInheritance(parent, args.side, setValue);
  }, [args.existingInvoice, args.parents, args.prefillParentId, args.side, getValues, setValue]);
  return form;
}

// The form fields the save gate watches, bundled so the dialog component
// stays within the size limit.
export function useWatchedNoteFields(form: ReturnType<typeof useAdjustmentForm>) {
  return {
    selectedId: useWatch({ control: form.control, name: 'adjusts_invoice_id' }),
    totalAmount: useWatch({ control: form.control, name: 'total_amount' }),
    lines: useWatch({ control: form.control, name: 'adjustment_lines' }),
    reason: useWatch({ control: form.control, name: 'note_reason' }),
    settlement: useWatch({ control: form.control, name: 'settlement_method' }),
  };
}
